import { Divider, Flex } from '@approvalmax/ui/src/components';
import { intl, mathService } from '@approvalmax/utils';
import { useSelector } from 'modules/react-redux';
import { memo, useMemo } from 'react';
import { SummaryLineHeaders, SummaryLineTotal, SummaryLineValues, SummarySection } from 'shared/components';

import { getQBooksContext } from '../../../../../selectors/qbooks';
import { calculateAmounts } from '../../QBooksJournalEntryRequestForm.utils';
import { messages } from './JournalEntrySummary.messages';
import { JournalEntrySummaryProps } from './JournalEntrySummary.types';

export const JournalEntrySummary = memo<JournalEntrySummaryProps>((props) => {
    const { lines, currency, lineAmountType, date } = props;

    const qbooksContext = useSelector(getQBooksContext);

    const { debitSubTotal, creditSubTotal, debitTotal, creditTotal, taxes } = useMemo(() => {
        return calculateAmounts(lines, lineAmountType, date, qbooksContext);
    }, [lines, lineAmountType, date, qbooksContext]);

    const showTotal = debitTotal !== creditTotal;

    return (
        <SummarySection>
            <Flex direction='column' spacing='4' width={420} justifyContent='end'>
                <SummaryLineHeaders
                    value1={`${messages.debitTitle} (${currency?.id})`}
                    value2={`${messages.creditTitle} (${currency?.id})`}
                />

                {Object.keys(taxes).length > 0 && (
                    <SummaryLineValues
                        label={messages.subtotalTitle}
                        value1={intl.formatNumber(debitSubTotal, 2)}
                        value2={intl.formatNumber(creditSubTotal, 2)}
                    />
                )}

                {Object.keys(taxes).map((tax) => (
                    <SummaryLineValues
                        key={tax}
                        label={taxes[tax].taxName}
                        value1={intl.formatNumber(taxes[tax].debit, 2)}
                        value2={intl.formatNumber(taxes[tax].credit, 2)}
                    />
                ))}

                <SummaryLineValues
                    label={messages.totalTitle}
                    value1={intl.formatNumber(debitTotal, 2)}
                    value2={intl.formatNumber(creditTotal, 2)}
                />

                {showTotal && <Divider color='midnight80' spacing='8 0' />}

                {showTotal && (
                    <SummaryLineTotal
                        label={messages.totalIsOutBy}
                        value1={intl.formatNumber(mathService.subtract(debitTotal, creditTotal), 2)}
                    />
                )}
            </Flex>
        </SummarySection>
    );
});

JournalEntrySummary.displayName = 'JournalEntrySummary';
